import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import {
  Activity,
  Phone,
  Camera,
  CalendarCheck,
  Film,
  MonitorPlay,
  Clock,
  ChevronDown,
  Link2,
  RefreshCw,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLaptop } from "@fortawesome/free-solid-svg-icons";
import { useCareLink360Summary, useCareLink360Sync } from "../../../hooks/useCareLink360";
import "./CareLink360Section.css";

// Report type → tab label, icon, and accent class (drives the soft-tint icon
// square and the active-tab underline, echoing the sidebar card rails).
const TABS = [
  { id: "activity", label: "Activity", Icon: Activity, accent: "teal", unit: "sessions" },
  { id: "call", label: "Calls", Icon: Phone, accent: "indigo", unit: "calls" },
  { id: "picture", label: "Pictures", Icon: Camera, accent: "pink", unit: "uploads" },
  { id: "schedule", label: "Schedule", Icon: CalendarCheck, accent: "amber", unit: "events" },
  { id: "video_gallery", label: "Video Gallery", Icon: Film, accent: "violet", unit: "videos" },
  { id: "video_meta", label: "Video Meta", Icon: MonitorPlay, accent: "cyan", unit: "plays" },
];

const fmtNum = (n) => Number(n || 0).toLocaleString();

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// "just now" / "12 min ago" / "3 h ago" / a date once it is older than a day.
const fmtSince = (iso) => {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "never";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return fmtDate(iso);
};

const fmtWait = (seconds) => {
  const mins = Math.ceil((Number(seconds) || 0) / 60);
  return mins > 1 ? `${mins} minutes` : "a minute";
};

// "Sync now" — asks the backend to pull fresh CareLink360 data for this client's
// agency. The pull runs in the background, so the button only reports that it
// started; the summary query polls until the new records land.
const SyncControl = ({ sync, mutation }) => {
  const running = !!sync?.inProgress || mutation.isPending;
  const failure = mutation.error?.response?.data;

  let note = null;
  if (failure?.status === "cooldown") {
    note = `Synced recently — try again in ${fmtWait(failure.cooldownSeconds)}.`;
  } else if (failure) {
    note = failure.message || "Couldn't start the sync.";
  } else if (running) {
    note = "Fetching from CareLink360 — this can take a few minutes.";
  } else if (sync?.lastSyncAt) {
    note = `Last synced ${fmtSince(sync.lastSyncAt)}`;
  }

  return (
    <div className="cl360-sync">
      <button
        type="button"
        className="cl360-sync-btn"
        onClick={() => mutation.mutate()}
        disabled={running}
      >
        <RefreshCw size={13} className={running ? "cl360-spin" : undefined} />
        {running ? "Syncing…" : "Sync now"}
      </button>
      {note ? (
        <span className={`cl360-sync-note ${failure ? "cl360-sync-note-error" : ""}`}>{note}</span>
      ) : null}
    </div>
  );
};

const HeadlineTiles = ({ items = [] }) => (
  <div className="cl360-tiles">
    {items.map((tile) => (
      <div className="cl360-tile" key={tile.label}>
        <div className="cl360-tile-value">
          {fmtNum(tile.value)}
          {tile.unit ? <span className="cl360-tile-unit">{tile.unit}</span> : null}
        </div>
        <div className="cl360-tile-label">{tile.label}</div>
      </div>
    ))}
  </div>
);

// Two-segment stacked bar for outgoing/incoming and confirmed/unconfirmed.
const SplitBar = ({ split, aLabel, bLabel, aKey, bKey }) => {
  const a = split[aKey] || 0;
  const b = split[bKey] || 0;
  const total = a + b;
  if (!total) return null;
  const aPct = Math.round((a / total) * 100);
  return (
    <div className="cl360-split">
      <div className="cl360-split-bar" role="img" aria-label={`${aLabel} ${a}, ${bLabel} ${b}`}>
        <span className="cl360-split-a" style={{ width: `${aPct}%` }} />
        <span className="cl360-split-b" style={{ width: `${100 - aPct}%` }} />
      </div>
      <div className="cl360-split-legend">
        <span><i className="cl360-dot cl360-dot-a" />{aLabel} <b>{fmtNum(a)}</b></span>
        <span><i className="cl360-dot cl360-dot-b" />{bLabel} <b>{fmtNum(b)}</b></span>
      </div>
    </div>
  );
};

const ColumnTooltip = ({ active, payload, unit }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="cl360-chart-tip">
      <div className="cl360-chart-tip-month">
        {point.label} {point.year}
        {point.current ? " · to date" : ""}
      </div>
      <div className="cl360-chart-tip-value">
        {fmtNum(point.count)} <span>{unit}</span>
      </div>
      {point.minutes ? (
        <div className="cl360-chart-tip-sub">{fmtNum(point.minutes)} min active</div>
      ) : null}
    </div>
  );
};

// The selected report's own month-by-month shape, on the same window as the
// matrix above. The tiles give the window total; this gives its trend — read on
// a real axis rather than the matrix's row-scaled cells.
//
// The final column is the current month and still filling, so it carries the
// solid accent while closed months sit at a lighter tint — the same convention
// the matrix uses, restated in the hint text so the distinction is never
// colour-alone.
const MonthlyColumns = ({ report, label, unit, filtered = false }) => {
  const points = report?.monthly?.points || [];

  // Records exist but none land in the charted window — every one of them is
  // older than it. Say so rather than dropping the chart silently: a missing
  // panel reads as "no data at all", which contradicts the count on the tab.
  if (points.length && !points.some((p) => p.count)) {
    return (
      <div className="cl360-columns">
        <div className="cl360-columns-head">
          <div className="cl360-breakdown-title">{label} by month</div>
        </div>
        <p className="cl360-columns-empty">
          {filtered ? (
            <>
              No {unit} of this type in the charted window. Its{" "}
              {fmtNum(report?.total)} records fall outside it.
            </>
          ) : (
            <>
              No {unit} in the last {points.length} months. This report&apos;s{" "}
              {fmtNum(report?.total)} records all predate the charted window.
            </>
          )}
        </p>
      </div>
    );
  }

  if (!points.some((p) => p.count)) return null;

  const data = points.map((p, i) => ({
    month: p.month,
    label: p.label,
    year: p.year,
    count: p.count || 0,
    minutes: p.minutes || 0,
    current: i === points.length - 1,
  }));

  return (
    <div className="cl360-columns">
      <div className="cl360-columns-head">
        <div className="cl360-breakdown-title">{label} by month</div>
        <span className="cl360-columns-hint">
          {unit} per month · current month to date
        </span>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        {/* Grid, axis and bar colours are set in CSS, not through these props —
            they have to follow the tab accent and the dark-mode tokens, and a
            var() inside an SVG presentation attribute doesn't resolve
            everywhere. */}
        <BarChart data={data} margin={{ top: 8, right: 4, left: -14, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tickLine={false} />
          <YAxis allowDecimals={false} width={46} tickFormatter={fmtNum} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ radius: 6 }} content={<ColumnTooltip unit={unit} />} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={54}>
            {data.map((d) => (
              <Cell key={d.month} className={d.current ? "cl360-bar-current" : "cl360-bar-past"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const DESCRIPTIONS = {
  activity: {
    summary:
      "Sessions where the client used the CareLink360 device — picking it up, navigating it, or leaving it open on a screen.",
    why: [
      "Device use is a rough proxy for engagement and routine. A steady month-to-month pattern that suddenly drops is worth a call, though it can equally mean a flat battery, a change in living arrangements, or a family member who has stopped visiting.",
      "It says nothing about health on its own. Read it beside the PacSana readings rather than in place of them.",
    ],
  },
  call: {
    summary:
      "Calls placed from and received on the device, with total talk time across the window.",
    why: [
      "The outgoing/incoming split is the useful part: a client who only ever receives calls is being kept in touch with, while one who initiates them is reaching out. A shift from the first pattern to the second, or back, tends to precede other changes.",
      "Talk time counts connected minutes, so a run of missed or very short calls shows as calls without matching minutes.",
    ],
  },
  picture: {
    summary: "Photos shared to the device by family members and care staff.",
    why: [
      "This is a measure of the people around the client, not of the client. A gallery that stops filling usually means family contact has tailed off — often the earliest visible sign of social isolation.",
    ],
  },
  schedule: {
    summary:
      "Calendar events sent to the device — appointments, visit reminders and medication prompts — and whether each was confirmed.",
    why: [
      "Unconfirmed events are the signal here. A rising share means prompts are being missed or ignored, which is worth checking before it shows up as a missed appointment.",
      "Schedule entries are dated forward, so this report's chart runs into future months while the others look back.",
    ],
  },
  video_gallery: {
    summary: "Videos available in the client's gallery, added by family or care staff.",
    why: [
      "Like pictures, this reflects what the people around the client are sending in. It counts what is available to watch — whether any of it was actually played is the Video Meta report.",
    ],
  },
  video_meta: {
    summary: "Playback events — videos the client actually opened and watched.",
    why: [
      "Read against Video Gallery: a full gallery with no playbacks means content is arriving but not reaching the client, which usually points at the device or at how it has been set up rather than at the family.",
    ],
  },
};

/**
 * Per-report guidance, matching the PacSana panel's block. Open by default —
 * these are device logs whose meaning is not obvious from the chart alone — and
 * collapsible for anyone who only wants the numbers.
 */
const DescriptionPanel = ({ config }) => {
  const [open, setOpen] = useState(true);
  if (!config) return null;

  return (
    <div className={`cl360-desc ${open ? "open" : ""}`}>
      <div className="cl360-desc-head">
        <span className="cl360-desc-title">Description</span>
        <button
          type="button"
          className="cl360-desc-toggle"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? "Hide" : "Show"}
          <ChevronDown size={18} className="cl360-desc-chevron" />
        </button>
      </div>

      {open ? (
        <div className="cl360-desc-body">
          <p className="cl360-desc-summary">{config.summary}</p>
          {config.why?.length ? (
            <>
              <h4 className="cl360-desc-h">Why it matters</h4>
              {config.why.map((text) => (
                <p key={text}>{text}</p>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const ALL_TYPES = "__all__";

/**
 * Narrows a schedule report to one event type.
 *
 * The figures a type changes are its own: how many events, how many have come
 * round, how many were confirmed, and the shape of its months. Everything else
 * on the panel — the window the chart is drawn on, which bucket is the current
 * month, when the report was last recorded — belongs to the report and is left
 * alone, so switching type redraws the bars without moving the axis under them.
 */
const applyEventType = (report, type) => {
  if (!type || type.key === ALL_TYPES) return report;
  const points = (report.monthly?.points || []).map((point) => ({
    ...point,
    count: type.byMonth?.[point.month] || 0,
    // Minutes are not broken down by type, and carrying the report's total into
    // a single type's tooltip would overstate it.
    minutes: 0,
  }));
  const unconfirmed = Math.max(0, type.occurred - type.confirmed);
  return {
    ...report,
    total: type.total,
    headline: [
      { label: "Events", value: type.total },
      {
        label: "Confirmed",
        value: type.occurred ? Math.round((type.confirmed / type.occurred) * 100) : 0,
        unit: "%",
        note: `${type.confirmed} of ${type.occurred} events to date`,
      },
    ],
    split: { confirmed: type.confirmed, unconfirmed, upcoming: type.upcoming },
    monthly: {
      ...report.monthly,
      points,
      windowCount: points.reduce((sum, point) => sum + point.count, 0),
    },
  };
};

/** Event-type filter for the schedule report, sitting above the figures it narrows. */
const EventTypeFilter = ({ types, value, onChange }) => (
  <div className="cl360-filter">
    <label className="cl360-filter-label" htmlFor="cl360-event-type">
      Event type
    </label>
    <div className="cl360-filter-control">
      <select
        id="cl360-event-type"
        className="cl360-filter-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value={ALL_TYPES}>All types ({fmtNum(types.reduce((n, t) => n + t.total, 0))})</option>
        {types.map((type) => (
          <option key={type.key} value={type.key}>
            {type.label} ({fmtNum(type.total)})
          </option>
        ))}
      </select>
      <ChevronDown size={16} className="cl360-filter-chevron" aria-hidden="true" />
    </div>
  </div>
);

const TabPanel = ({ report, reportId, label, unit }) => {
  const [eventType, setEventType] = useState(ALL_TYPES);
  const types = report?.eventTypes || [];
  const selected = types.find((t) => t.key === eventType) || null;
  const shown = useMemo(() => applyEventType(report, selected), [report, selected]);

  if (!report || !report.total) {
    return (
      <div className="cl360-tab-empty">
        <p>No records for this report yet.</p>
        <span>Data appears here after the next CareLink360 sync.</span>
      </div>
    );
  }
  return (
    <div className="cl360-tab-body">
      {types.length > 1 ? (
        <EventTypeFilter types={types} value={eventType} onChange={setEventType} />
      ) : null}

      <HeadlineTiles items={shown.headline} />

      <MonthlyColumns
        report={shown}
        label={selected ? selected.label : label}
        unit={unit}
        filtered={Boolean(selected)}
      />

      {shown.split && ("outgoing" in shown.split) ? (
        <SplitBar split={shown.split} aKey="outgoing" bKey="incoming" aLabel="Outgoing" bLabel="Incoming" />
      ) : null}
      {shown.split && ("confirmed" in shown.split) ? (
        <SplitBar split={shown.split} aKey="confirmed" bKey="unconfirmed" aLabel="Confirmed" bLabel="Unconfirmed" />
      ) : null}

      <DescriptionPanel config={DESCRIPTIONS[reportId]} />

      <div className="cl360-tab-foot">
        <Clock size={13} />
        Last recorded {fmtDate(report.lastAt)}
      </div>
    </div>
  );
};

const CareLink360Section = ({ clientId }) => {
  const [activeTab, setActiveTab] = useState("activity");
  const { data, isLoading, isError } = useCareLink360Summary(clientId);
  const syncMutation = useCareLink360Sync(clientId);

  // The sync control only appears once a CareLink360 person is linked — there is
  // nothing for an unlinked client to refresh.
  const header = (
    <div className="cl360-header">
      <div>
        <h2 className="cl360-title">
          <FontAwesomeIcon icon={faLaptop} style={{ marginRight: 8 }} />
          CareLink 360
        </h2>
        <p className="cl360-subtitle">
          Activity, calls, media and schedule adherence synced from the client&apos;s
          CareLink360 device.
        </p>
      </div>
      <div className="cl360-header-actions">
        <span className="cl360-source-badge">From CareLink360</span>
        {data?.linked ? <SyncControl sync={data.sync} mutation={syncMutation} /> : null}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="cl360-section">
        {header}
        <div className="cl360-state">Loading CareLink360 activity…</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="cl360-section">
        {header}
        <div className="cl360-state cl360-state-error">
          Couldn&apos;t load CareLink360 activity for this client.
        </div>
      </div>
    );
  }

  if (!data || !data.linked) {
    return (
      <div className="cl360-section">
        {header}
        <div className="cl360-state cl360-state-unlinked">
          <Link2 size={18} />
          <div>
            <strong>No CareLink360 device linked to this client.</strong>
            <span>
              A CareLink360 record matches this client by name once their reports
              sync. Link one manually from the CareLink360 admin if it doesn&apos;t
              match automatically.
            </span>
          </div>
        </div>
      </div>
    );
  }

  const { totalRecords, lastActivityAt, reports } = data;
  const activeReport = reports?.[activeTab];
  const activeTabConfig = TABS.find((t) => t.id === activeTab);

  return (
    <div className="cl360-section">
      {header}

      {/* Top-line rollup. The linked person's name and username are deliberately
          not shown — the panel already sits inside that client's record. */}
      <div className="cl360-identity">
        <div className="cl360-identity-chips">
          <span className="cl360-chip cl360-chip-lead">{fmtNum(totalRecords)} records</span>
          <span className="cl360-chip cl360-chip-muted">
            Last activity {fmtDate(lastActivityAt)}
          </span>
        </div>
      </div>

      {/* Report-type tabs */}
      <div className="cl360-tabs" role="tablist">
        {TABS.map(({ id, label, Icon, accent }) => {
          const count = reports?.[id]?.total || 0;
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`cl360-tab cl360-accent-${accent} ${isActive ? "active" : ""}`}
              onClick={() => setActiveTab(id)}
            >
              <span className="cl360-tab-icon"><Icon size={15} /></span>
              <span className="cl360-tab-label">{label}</span>
              <span className="cl360-tab-count">{fmtNum(count)}</span>
            </button>
          );
        })}
      </div>

      <div className={`cl360-tab-pane cl360-accent-${activeTabConfig?.accent}`}>
        <TabPanel
          report={activeReport}
          reportId={activeTab}
          label={activeTabConfig?.label}
          unit={activeTabConfig?.unit}
        />
      </div>
    </div>
  );
};

export default CareLink360Section;
